import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { flowRight as compose } from 'lodash';
import Col from 'react-bootstrap/lib/Col';
import Panel from 'react-bootstrap/lib/Panel';
import Badge from 'react-bootstrap/lib/Badge';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import InboxItem from './InboxItem';
import EmptyInbox from './EmptyInbox';
import Loader from '../Loader';
import CustomPagination from '../CustomPagination';

import UnreadCountQuery from './UnreadCount.graphql';
import GetAllThreadQuery from './AllThreadsQuery.graphql';
import messages from '../../locale/jsonMessages';
import { withQuery } from '../../hoc/withGraphql';

import cs from '../../components/commonStyle.css';
import s from './Inbox.css';

const Inbox = ({
  allThreads: { refetch, loading, GetAllThreads },
  UnreadCount: { getUnreadCount }
}) => {

  const { formatMessage } = useIntl();

  const [type, setType] = useState('renter');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const inboxLoader = useSelector(state => state?.loader?.inbox);

  useEffect(() => {
    if (inboxLoader) {
      setCurrentPage(1);
      refetch({ currentPage: 1 });
    }
  }, [refetch, inboxLoader]);

  const changeType = async (threadType) => {
    setIsDataLoading(true)
    setCurrentPage(1);
    setType(threadType)
    await refetch({ threadType, currentPage: 1 });
    setIsDataLoading(false)
  }

  const paginationData = (currentPage) => {
    setCurrentPage(currentPage)
    refetch({ currentPage });
  }

  let hostActive, guestActive, host, guest;
  if (type === 'owner') {
    hostActive = s.active;
  } else {
    guestActive = s.active;
  }
  if (getUnreadCount) {
    host = getUnreadCount.hostCount != null && getUnreadCount.hostCount > 0 ? getUnreadCount.hostCount : null;
    guest = getUnreadCount.guestCount != null && getUnreadCount.guestCount > 0 ? getUnreadCount.guestCount : null;
  }
  return (
    <Col xs={12} sm={12} md={12} lg={12}>
      <div className={cx(cs.spaceTop6, 'whiteBgColor', 'youcarsBg')}>
        <ul className={cx(cs.listContainer, cs.dFlex, cs.mobileFlexContainer)}>
          <li className={guestActive}>
            <a
              className={cx(s.tabItem, cs.listTabItem, 'tabLinkRTL', 'textWhite')}
              onClick={() => changeType('renter')}
            >
              <span className={cx(s.inboxHeadingText)}>
                <FormattedMessage {...messages.traveling} />
                {
                  guest != null && <Badge
                    className={cx(s.count, 'countRTL')}
                  >
                    {guest}
                  </Badge>
                }
              </span>
            </a>
          </li>
          <li className={hostActive}>
            <a
              className={cx(s.tabItem, cs.listTabItem, 'tabLinkRTL', 'textWhite')}
              onClick={() => changeType('owner')}
            >
              <span className={cx(s.inboxHeadingText)}>
                <FormattedMessage {...messages.hosting} />
                {
                  host != null && <Badge
                    className={cx(s.count, 'countRTL')}
                  >
                    {host}
                  </Badge>
                }
              </span>
            </a>
          </li>
        </ul>
        <Panel className={cx(cs.spaceTop4, cs.noMarginBottom, s.panelHeader)}>
          {
            (loading || isDataLoading) && <Loader type={"text"} />
          }
          {
            !loading && !isDataLoading && GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length > 0
            && <ul className={cx(s.listLayout, 'listContainerRTL')}>
              {
                GetAllThreads.threadsData.map((item, index) => {
                  if (item && item.guestProfile && item.hostProfile) {
                    return <InboxItem
                      key={index}
                      threadId={item.id}
                      type={type}
                      profileId={type === 'owner' ? item.guestProfile.profileId : item.hostProfile.profileId}
                      picture={type === 'owner' ? item.guestProfile.picture : item.hostProfile.picture}
                      displayName={type === 'owner' ? item.guestProfile.displayName : item.hostProfile.displayName}
                      firstName={type === 'owner' ? item.guestProfile.firstName : item.hostProfile.firstName}
                      content={item.threadItem && item.threadItem.content}
                      createdAt={item.threadItem && item.threadItem.createdAt}
                      city={item.listData && item.listData.city}
                      state={item.listData && item.listData.state}
                      country={item.listData && item.listData.country}
                      zipcode={item.listData && item.listData.zipcode}
                      startDate={item.threadItem && item.threadItem.startDate}
                      endDate={item.threadItem && item.threadItem.endDate}
                      status={item.threadItem && item.threadItem.type}
                      sentBy={item.threadItem && item.threadItem.sentBy}
                      read={item.threadItem && item.threadItem.isRead}
                      startTime={item.threadItem && item.threadItem.startTime}
                      endTime={item.threadItem && item.threadItem.endTime}
                    />
                  } else {
                    return <li />
                  }
                })
              }
            </ul>
          }
          {
            !loading && GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length === 0
            && <EmptyInbox type={type} />
          }
          {
            !isDataLoading && GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length > 0
            && <div>
              <CustomPagination
                total={GetAllThreads.count}
                currentPage={currentPage}
                defaultCurrent={1}
                defaultPageSize={5}
                change={paginationData}
                paginationLabel={formatMessage(messages.messages)}
              />
            </div>
          }
        </Panel>
      </div>
    </Col>
  );
}

Inbox.propTypes = {
  formatMessage: PropTypes.any,
  allThreads: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    GetAllThreads: PropTypes.shape({
      count: PropTypes.number.isRequired,
      threadsData: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        listData: PropTypes.shape({
          city: PropTypes.string.isRequired,
          state: PropTypes.string.isRequired,
          country: PropTypes.string.isRequired,
        }),
        guestProfile: PropTypes.shape({
          profileId: PropTypes.number.isRequired,
          picture: PropTypes.string,
          displayName: PropTypes.string.isRequired,
        }),
        hostProfile: PropTypes.shape({
          profileId: PropTypes.number.isRequired,
          picture: PropTypes.string,
          displayName: PropTypes.string.isRequired,
        }),
        threadItem: PropTypes.shape({
          type: PropTypes.string.isRequired,
          content: PropTypes.string,
          startDate: PropTypes.string,
          endDate: PropTypes.string,
          isRead: PropTypes.bool.isRequired,
          sentBy: PropTypes.string.isRequired,
          createdAt: PropTypes.string.isRequired,
        })
      }))
    }),
  }),
  UnreadCount: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    getUnreadCount: PropTypes.shape({
      hostCount: PropTypes.number.isRequired,
      guestCount: PropTypes.number.isRequired
    })
  })
};

Inbox.defaultProps = {
  allThreads: {
    loading: true,
    GetAllThreads: {
      count: null,
      threadsData: []
    }
  },
  UnreadCount: {
    loading: true,
    hostCount: null,
    guestCount: null
  }
};

export default compose(
  withStyles(cs, s),
  withQuery(UnreadCountQuery, {
    name: 'UnreadCount',
    options: {
      ssr: false,
      fetchPolicy: 'network-only',
    }
  }),
  withQuery(GetAllThreadQuery, {
    name: 'GetAllThreads',
    options: {
      ssr: false,
      fetchPolicy: 'network-only',
    }
  })
)(Inbox);
